import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { Star, Award, Loader2, TrendingUp, Clock, Zap, FileText, LineChart as LineIcon, Medal } from "lucide-react";
import { GradeTrendChart } from "@/components/GradeTrendChart";

const BADGE_ICONS = { Clock, Zap, FileText, Award, Medal };

export default function Grades() {
  const [data, setData] = useState(null);
  const [graded, setGraded] = useState([]);
  const [trend, setTrend] = useState(null);
  const [err, setErr] = useState(false);
  const navigate = useNavigate();

  const load = () => {
    setErr(false);
    Promise.all([api.get("/dashboard/murid"), api.get("/assignments"), api.get("/me/trend")]).then(([d, a, t]) => {
      setData(d.data);
      setGraded(a.data.filter((x) => x.my_final_score != null));
      setTrend(t.data);
    }).catch(() => setErr(true));
  };
  useEffect(() => { load(); }, []);

  if (err) return (
    <div className="max-w-md mx-auto text-center py-20 space-y-4" data-testid="grades-error">
      <p className="text-slate-500">Gagal memuat nilai. Coba lagi ya.</p>
      <button onClick={load} className="h-11 px-6 rounded-xl bg-teal-600 hover:bg-teal-700 text-white font-semibold transition-colors" data-testid="grades-retry-btn">Coba Lagi</button>
    </div>
  );
  if (!data) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-teal-600" /></div>;

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-800">Nilai & Bintang</h1>
        <p className="text-slate-500 mt-1">Pencapaian pribadimu. Terus semangat belajar ya! 🌟</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <div className="bg-amber-50 rounded-2xl p-5 border border-amber-100 text-center">
          <Star className="w-7 h-7 text-amber-500 mx-auto" />
          <p className="font-display text-3xl font-bold text-amber-600 mt-1">{data.points}</p>
          <p className="text-xs text-amber-700">Bintang Belajar</p>
        </div>
        <div className="bg-indigo-50 rounded-2xl p-5 border border-indigo-100 text-center">
          <TrendingUp className="w-7 h-7 text-indigo-500 mx-auto" />
          <p className="font-display text-3xl font-bold text-indigo-600 mt-1">{data.average_score ?? "—"}</p>
          <p className="text-xs text-indigo-700">Rata-rata Nilai</p>
        </div>
        <div className="bg-emerald-50 rounded-2xl p-5 border border-emerald-100 text-center col-span-2 sm:col-span-1">
          <Award className="w-7 h-7 text-emerald-500 mx-auto" />
          <p className="font-display text-3xl font-bold text-emerald-600 mt-1">{(data.badges || []).length}</p>
          <p className="text-xs text-emerald-700">Lencana</p>
        </div>
      </div>

      <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-9 h-9 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center"><LineIcon className="w-5 h-5" /></div>
          <div>
            <h3 className="font-display text-lg font-semibold text-slate-800">Perkembangan Nilaimu</h3>
            <p className="text-xs text-slate-500">Garis naik artinya kamu makin hebat!</p>
          </div>
        </div>
        <GradeTrendChart data={trend} emptyText="Belum ada nilai. Ayo kerjakan tugasmu!" testId="murid-trend" />
      </div>

      <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
        <h3 className="font-display text-lg font-semibold text-slate-800 mb-4">Lencana Prestasi</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {Object.entries(data.badge_defs).map(([id, def]) => {
            const Icon = BADGE_ICONS[def.icon] || Award;
            const earned = (data.badges || []).includes(id);
            return (
              <div key={id} data-testid={`grades-badge-${id}`} className={`rounded-2xl p-4 text-center border ${earned ? "bg-amber-50 border-amber-200" : "bg-slate-50 border-slate-100 opacity-50 grayscale"}`}>
                <div className={`w-11 h-11 rounded-full mx-auto flex items-center justify-center ${earned ? "bg-amber-400 text-white" : "bg-slate-200 text-slate-400"}`}><Icon className="w-5 h-5" /></div>
                <p className="text-xs font-semibold text-slate-700 mt-2">{def.name}</p>
              </div>
            );
          })}
        </div>
      </div>

      <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
        <h3 className="font-display text-lg font-semibold text-slate-800 mb-4">Nilai Tugas</h3>
        {graded.length === 0 ? (
          <p className="text-sm text-slate-400">Belum ada nilai final. Kerjakan tugasmu dulu ya!</p>
        ) : (
          <div className="divide-y divide-slate-50">
            {graded.map((g) => (
              <button key={g.id} onClick={() => navigate(`/murid/tugas/${g.id}`)} className="w-full flex items-center justify-between py-3 text-left hover:bg-slate-50 rounded-lg px-2 transition-colors" data-testid={`grade-row-${g.id}`}>
                <div>
                  <p className="font-medium text-slate-800">{g.title}</p>
                  <p className="text-xs text-slate-500">{g.subject_name}</p>
                </div>
                <span className={`font-display text-2xl font-bold ${g.my_final_score >= 75 ? "text-emerald-600" : "text-orange-500"}`}>{g.my_final_score}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
